import { useEffect, useMemo, useState } from "react";
import { addDoc, collection, deleteDoc, doc, onSnapshot, orderBy, query, serverTimestamp, setDoc, Timestamp, where } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { getApp } from "firebase/app";
import { db } from "../../firebase";
import { useBooking } from "./hooks/useBooking";
import { getClient, getClientBookings, findMatchingClients, createClient } from "./clientResolver";
import QuickUpdateModal from "./QuickUpdateModal";
import CorrespondencePanel from "./CorrespondencePanel";
import AddressAutocomplete from "../../components/AddressAutocomplete";
import {
  STATUS_LABELS,
  EVENT_TYPE_LABELS,
  TABS,
  ENUM_OPTIONS,
  BOOKING_SOURCE,
  defaultBooking,
} from "./schema";

import { STAFF_THEME, FONT } from "../../theme/staff";
const BRAND = { ...STAFF_THEME, bg: STAFF_THEME.dark, line: STAFF_THEME.raised, red: "#c94444" };

// ---------- value helpers ----------

function tsToDate(v) {
  if (!v) return null;
  if (v && typeof v.toDate === "function") return v.toDate();
  if (v instanceof Date) return v;
  if (typeof v === "string") return new Date(v);
  if (typeof v === "number") return new Date(v);
  return null;
}

function toDateInputValue(v) {
  const d = tsToDate(v);
  if (!d || isNaN(d)) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function toDatetimeInputValue(v) {
  const d = tsToDate(v);
  if (!d || isNaN(d)) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromDateInputValue(s) {
  if (!s) return null;
  const d = new Date(s + "T00:00:00");
  return isNaN(d) ? null : Timestamp.fromDate(d);
}

function fromDatetimeInputValue(s) {
  if (!s) return null;
  const d = new Date(s);
  return isNaN(d) ? null : Timestamp.fromDate(d);
}

function toNumberOrNull(s) {
  if (s === "" || s == null) return null;
  const n = typeof s === "string" ? parseFloat(s) : s;
  return Number.isFinite(n) ? n : null;
}

// Build a pre-filled Google Calendar event URL (opens in new tab, no backend needed)
function buildGoogleCalendarUrl(booking) {
  const start = tsToDate(booking.confirmedStart);
  const end = tsToDate(booking.confirmedEnd);
  const day = tsToDate(booking.confirmedDate);

  if (!start && !day) return null;

  const pad = (n) => String(n).padStart(2, "0");
  const fmtDateTime = (d) => `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}T${pad(d.getHours())}${pad(d.getMinutes())}00`;
  const fmtDate = (d) => `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`;

  let dates;
  if (start && end && end > start) {
    dates = `${fmtDateTime(start)}/${fmtDateTime(end)}`;
  } else if (day) {
    const next = new Date(day);
    next.setDate(next.getDate() + 1);
    dates = `${fmtDate(day)}/${fmtDate(next)}`;
  } else {
    return null;
  }

  const title = booking.eventName
    ? `${booking.eventName} — ${booking.company || booking.name || "JoP"}`
    : `JoP — ${booking.company || booking.name || "Booking"}`;

  const lines = [];
  if (booking.name) lines.push(`Client: ${booking.name}${booking.company ? ` (${booking.company})` : ""}`);
  if (booking.email) lines.push(`Email: ${booking.email}`);
  if (booking.phone) lines.push(`Phone: ${booking.phone}`);
  if (booking.attendance) lines.push(`Attendance: ${booking.attendance}`);
  if (booking.eventType) lines.push(`Event type: ${booking.eventType}`);
  if (booking.services) lines.push(`Services: ${booking.services}`);
  if (booking.totalRate) lines.push(`Rate: $${booking.totalRate}`);
  if (booking.dayOfContactName) lines.push(`Day-of contact: ${booking.dayOfContactName}${booking.dayOfContactPhone ? ` (${booking.dayOfContactPhone})` : ""}`);
  if (booking.id) lines.push(`\nCRM: https://thejoyofpancakes.com/crm/bookings/${booking.id}`);

  const location = [booking.venueName, booking.venueAddress].filter(Boolean).join(", ") || booking.location || "";

  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: title,
    dates,
    details: lines.join("\n"),
    location,
  });
  return `https://calendar.google.com/calendar/r/eventedit?${params.toString()}`;
}

// Shallow diff for dirty tracking + write payload. Only fields that differ
// from the canonical booking doc are written, so we don't churn updatedAt
// on every accidental focus.
// Fields managed server-side by Cloud Functions — never part of form dirty state.
const SERVER_MANAGED_FIELDS = new Set(["id", "tasks", "_taskCounts", "updatedAt", "emailLog", "lastEmailedAt"]);

function buildWritePayload(form, original) {
  const payload = {};
  for (const key of Object.keys(form)) {
    if (SERVER_MANAGED_FIELDS.has(key)) continue;
    const a = form[key];
    const b = original ? original[key] : undefined;
    // Compare Timestamps by millis
    const aMillis = a && typeof a.toMillis === "function" ? a.toMillis() : a;
    const bMillis = b && typeof b.toMillis === "function" ? b.toMillis() : b;
    if (aMillis !== bMillis && !(aMillis == null && bMillis == null)) {
      payload[key] = a;
    }
  }
  return payload;
}

function isDirty(form, original) {
  return Object.keys(buildWritePayload(form, original)).length > 0;
}

// ---------- input renderers ----------

const inputStyle = {
  background: BRAND.bg,
  border: `1px solid ${BRAND.line}`,
  borderRadius: "6px",
  color: BRAND.text,
  padding: "0.5rem 0.65rem",
  fontSize: "0.9rem",
  width: "100%",
  outline: "none",
  boxSizing: "border-box",
};

function FlashProfileSelect({ id, value, onChange }) {
  const [profiles, setProfiles] = useState(null);
  useEffect(() => {
    const q = query(collection(db, "flashDesignProfiles"), orderBy("name"));
    return onSnapshot(q, snap => {
      setProfiles(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }, () => setProfiles([]));
  }, []);
  if (profiles == null) {
    return <div style={{ ...inputStyle, color: BRAND.muted }}>Loading flash sets…</div>;
  }
  return (
    <select
      id={id}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value || null)}
      style={inputStyle}
    >
      <option value="">— None (use base IP set only) —</option>
      {profiles.map(p => (
        <option key={p.id} value={p.id}>
          {p.name}{p.clientName ? ` — ${p.clientName}` : ""} ({(p.designs || []).length})
        </option>
      ))}
    </select>
  );
}

function Field({ label, type, value, onChange, options }) {
  const id = "f_" + label.replace(/\s+/g, "_").toLowerCase();

  if (type === "bool") {
    return (
      <label htmlFor={id} style={{ display: "flex", alignItems: "center", gap: "0.5rem", color: BRAND.cream, fontSize: "0.88rem" }}>
        <input
          id={id}
          type="checkbox"
          checked={!!value}
          onChange={(e) => onChange(e.target.checked)}
        />
        {label}
      </label>
    );
  }

  const wrap = (input) => (
    <div>
      <label htmlFor={id} style={{ display: "block", fontSize: "0.72rem", color: BRAND.muted, marginBottom: "0.2rem", letterSpacing: "0.02em" }}>
        {label}
      </label>
      {input}
    </div>
  );

  if (type === "textarea") {
    return wrap(
      <textarea
        id={id}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value || null)}
        rows={3}
        style={{ ...inputStyle, resize: "vertical", fontFamily: "inherit" }}
      />
    );
  }

  if (type === "number") {
    return wrap(
      <input
        id={id}
        type="number"
        value={value == null ? "" : value}
        onChange={(e) => onChange(toNumberOrNull(e.target.value))}
        style={inputStyle}
      />
    );
  }

  if (type === "date") {
    return wrap(
      <input
        id={id}
        type="date"
        value={toDateInputValue(value)}
        onChange={(e) => onChange(fromDateInputValue(e.target.value))}
        style={inputStyle}
      />
    );
  }

  if (type === "datetime") {
    return wrap(
      <input
        id={id}
        type="datetime-local"
        value={toDatetimeInputValue(value)}
        onChange={(e) => onChange(fromDatetimeInputValue(e.target.value))}
        style={inputStyle}
      />
    );
  }

  if (type && type.startsWith("enum:")) {
    const key = type.slice(5);
    const opts = options || ENUM_OPTIONS[key] || [];
    return wrap(
      <select
        id={id}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value || null)}
        style={inputStyle}
      >
        <option value="">—</option>
        {opts.map(([v, label]) => (
          <option key={v} value={v}>{label}</option>
        ))}
      </select>
    );
  }

  if (type === "address") {
    return wrap(
      <AddressAutocomplete
        value={value ?? ""}
        onChange={(v) => onChange(v || null)}
        inputStyle={inputStyle}
      />
    );
  }

  if (type === "ref:flashDesignProfile") {
    return wrap(
      <FlashProfileSelect id={id} value={value} onChange={onChange} />
    );
  }

  // text / email / tel
  return wrap(
    <input
      id={id}
      type={type === "email" ? "email" : type === "tel" ? "tel" : "text"}
      value={value ?? ""}
      onChange={(e) => onChange(e.target.value || null)}
      style={inputStyle}
    />
  );
}

// Time-only field that anchors on a sibling date (confirmedDate). Stores a
// full Timestamp underneath — the date portion is always the anchor's date,
// the H:M is the only thing this input controls. If the anchor is null, the
// input is disabled with a hint to set the date first.
function TimeField({ label, value, onChange, anchorDate }) {
  const id = "f_" + label.replace(/\s+/g, "_").toLowerCase();
  const anchor = tsToDate(anchorDate);
  const hasAnchor = !!anchor && !isNaN(anchor);

  const v = tsToDate(value);
  const timeStr = (v && !isNaN(v))
    ? `${String(v.getHours()).padStart(2, "0")}:${String(v.getMinutes()).padStart(2, "0")}`
    : "";

  const onTimeChange = (e) => {
    const t = e.target.value; // "HH:MM" or ""
    if (!t) { onChange(null); return; }
    if (!hasAnchor) return;
    const [h, m] = t.split(":").map((n) => parseInt(n, 10));
    if (!Number.isFinite(h) || !Number.isFinite(m)) return;
    const next = new Date(anchor);
    next.setHours(h, m, 0, 0);
    onChange(Timestamp.fromDate(next));
  };

  return (
    <div>
      <label htmlFor={id} style={{ display: "block", fontSize: "0.72rem", color: BRAND.muted, marginBottom: "0.2rem", letterSpacing: "0.02em" }}>
        {label}
      </label>
      <input
        id={id}
        type="time"
        value={timeStr}
        onChange={onTimeChange}
        disabled={!hasAnchor}
        title={hasAnchor ? undefined : "Set a confirmed date first"}
        style={{ ...inputStyle, opacity: hasAnchor ? 1 : 0.55 }}
      />
      {!hasAnchor && (
        <div style={{ color: BRAND.muted, fontSize: "0.7rem", marginTop: "0.2rem", fontStyle: "italic" }}>
          Set the confirmed date first
        </div>
      )}
    </div>
  );
}

// Tabs hide the other sections, so cards don't need to collapse anymore.
function SectionCard({ title, children }) {
  return (
    <div style={{
      background: BRAND.panel,
      border: `1px solid ${BRAND.line}`,
      borderRadius: "10px",
      marginBottom: "0.75rem",
      // overflow:visible so a field's autocomplete dropdown (publicLocation is
      // the last field in its section) isn't clipped by the card edge. No child
      // has a background fill that needs the rounded corners clipped.
      overflow: "visible",
    }}>
      <div style={{
        color: BRAND.gold,
        padding: "0.7rem 0.9rem",
        fontWeight: 700,
        fontSize: "0.8rem",
        letterSpacing: "0.06em",
        borderBottom: `1px solid ${BRAND.line}`,
      }}>
        {title.toUpperCase()}
      </div>
      <div style={{
        padding: "0.85rem 0.9rem",
        display: "grid",
        gap: "0.7rem",
        gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
        alignItems: "start",
      }}>
        {children}
      </div>
    </div>
  );
}

// Fields that should always span the full form width regardless of grid column.
const FULL_WIDTH_TYPES = new Set(["textarea"]);
const FULL_WIDTH_KEYS  = new Set(["clientNotes", "venueAddress", "publicDescription", "internalNotes", "flightInfo", "hotelInfo", "flashBoardNotes"]);
function fieldSpan(key, type) {
  return (FULL_WIDTH_TYPES.has(type) || FULL_WIDTH_KEYS.has(key)) ? "1 / -1" : "auto";
}

// Google Maps search URL for a free-form address.
function mapsUrl(address) {
  if (!address) return null;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`;
}

// ---------- Hero card (read-only summary) ----------

const STATUS_COLORS = {
  new_inquiry:      { bg: "#4aa4e1", fg: "#0a1a2e" },
  contacted:        { bg: "#b9d550", fg: "#0d1833" },
  quoted:           { bg: "#fcb31f", fg: "#0d1833" },
  deposit_received: { bg: "#f57600", fg: "#ffffff" },
  booked:           { bg: "#1f59db", fg: "#ffffff" },
  completed:        { bg: "#234080", fg: "#ffdaad" },
  declined:         { bg: "#6b2234", fg: "#ffdaad" },
  no_response:      { bg: "#6b2234", fg: "#ffdaad" },
  overdue:          { bg: "#ef537e", fg: "#ffffff" },
  payment_failed:   { bg: "#ef537e", fg: "#ffffff" },
  spam:             { bg: "#3a1424", fg: "rgba(255,255,255,0.5)" },
  hold:             { bg: "#8a6a00", fg: "#ffdaad" },
};

function formatDateTimeRange(booking) {
  const start = tsToDate(booking.confirmedStart);
  const end = tsToDate(booking.confirmedEnd);
  const day = tsToDate(booking.confirmedDate);
  if (start && end) {
    const sameDay = start.toDateString() === end.toDateString();
    const dateStr = start.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
    const startStr = start.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
    const endStr = end.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
    if (sameDay) return `${dateStr} · ${startStr} – ${endStr}`;
    const endDateStr = end.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });
    return `${dateStr} ${startStr} → ${endDateStr} ${endStr}`;
  }
  if (day) return day.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
  const inquiry = tsToDate(booking.inquiryEventDate);
  if (inquiry) return `${inquiry.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })} (inquiry)`;
  return "Date TBD";
}

function fmtMoney(n) {
  if (n == null || n === "") return null;
  const num = typeof n === "string" ? parseFloat(n) : n;
  if (!Number.isFinite(num)) return null;
  return "$" + num.toLocaleString("en-US", { maximumFractionDigits: 0 });
}

function HeroCard({ booking, isNew, onSaveField }) {
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState("");
  const [savingTitle, setSavingTitle] = useState(false);

  if (isNew) return null;
  const statusColor = STATUS_COLORS[booking.status] || STATUS_COLORS.new_inquiry;
  const locationStr = [booking.venueName, booking.venueAddress].filter(Boolean).join(", ") || booking.location || null;
  const mapsLink = mapsUrl(booking.venueAddress || booking.venueName || booking.location);
  const rate = fmtMoney(booking.totalRate) || fmtMoney(booking.quotedAmount);
  const depositDone = Boolean(booking.depositPaidAt) || booking.squareDepositStatus === "PAID";
  const usingNameFallback = !booking.eventName && Boolean(booking.name);

  const startTitleEdit = () => {
    if (!onSaveField) return;
    setTitleDraft(booking.eventName || "");
    setEditingTitle(true);
  };

  const commitTitle = async () => {
    if (savingTitle || !editingTitle) return;
    const next = titleDraft.trim() || null;
    if (next === (booking.eventName || null)) {
      setEditingTitle(false);
      return;
    }
    setSavingTitle(true);
    try {
      await onSaveField("eventName", next);
      setEditingTitle(false);
    } catch (err) {
      alert("Save failed: " + (err.message || "unknown"));
    } finally {
      setSavingTitle(false);
    }
  };

  const cancelTitleEdit = () => {
    setEditingTitle(false);
    setTitleDraft("");
  };

  return (
    <div style={{
      background: `linear-gradient(160deg, ${BRAND.panel} 0%, ${BRAND.bg} 100%)`,
      border: `1px solid ${BRAND.line}`,
      borderRadius: "14px",
      padding: "1rem 1.1rem",
      marginBottom: "0.85rem",
    }}>
      {/* Top row: event title + status pill */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: "0.6rem", marginBottom: "0.6rem" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ color: BRAND.muted, fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.08em", marginBottom: "0.15rem" }}>
            {booking.eventType ? (EVENT_TYPE_LABELS[booking.eventType] || booking.eventType).toUpperCase() : "EVENT"}
          </div>
          {editingTitle ? (
            <input
              autoFocus
              value={titleDraft}
              onChange={(e) => setTitleDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") { e.preventDefault(); commitTitle(); }
                else if (e.key === "Escape") { e.preventDefault(); cancelTitleEdit(); }
              }}
              onBlur={commitTitle}
              onFocus={(e) => e.target.select()}
              placeholder="Event name"
              disabled={savingTitle}
              style={{
                margin: 0, width: "100%",
                fontSize: "1.45rem", color: "white", lineHeight: 1.2,
                fontFamily: "inherit", fontWeight: 700,
                background: "rgba(0,0,0,0.35)",
                border: `1px solid ${BRAND.gold}`,
                borderRadius: "8px",
                padding: "0.25rem 0.45rem",
                outline: "none",
              }}
            />
          ) : (
            <h1
              onClick={startTitleEdit}
              title={onSaveField ? "Tap to edit event name" : undefined}
              style={{
                margin: 0, fontSize: "1.45rem", color: "white", lineHeight: 1.2,
                wordBreak: "break-word", cursor: onSaveField ? "pointer" : "default",
                display: "flex", alignItems: "baseline", gap: "0.4rem", flexWrap: "wrap",
              }}
            >
              <span style={{ fontStyle: usingNameFallback ? "italic" : "normal", color: usingNameFallback ? BRAND.cream : "white" }}>
                {booking.eventName || booking.name || "(untitled)"}
              </span>
              {onSaveField && (
                <span style={{ fontSize: "0.85rem", opacity: 0.55 }}>✏️</span>
              )}
            </h1>
          )}
          {!editingTitle && usingNameFallback && (
            <div style={{ color: BRAND.muted, fontSize: "0.7rem", marginTop: "0.2rem", fontStyle: "italic" }}>
              No event name set — tap title to add one
            </div>
          )}
        </div>
        <span style={{
          background: statusColor.bg, color: statusColor.fg,
          borderRadius: "999px", padding: "0.3rem 0.75rem",
          fontSize: "0.72rem", fontWeight: 800, letterSpacing: "0.04em",
          whiteSpace: "nowrap",
        }}>
          {STATUS_LABELS[booking.status] || "—"}
        </span>
      </div>

      {/* Date/time line */}
      <div style={{ color: BRAND.gold, fontWeight: 700, fontSize: "0.95rem", marginBottom: "0.35rem" }}>
        🗓 {formatDateTimeRange(booking)}
      </div>

      {/* Location line — clickable opens Maps */}
      {locationStr && (
        <div style={{ marginBottom: "0.35rem" }}>
          {mapsLink ? (
            <a href={mapsLink} target="_blank" rel="noopener noreferrer" style={{
              color: BRAND.sky, textDecoration: "none", fontWeight: 600, fontSize: "0.9rem",
              wordBreak: "break-word",
            }}>
              📍 {locationStr} ↗
            </a>
          ) : (
            <span style={{ color: BRAND.cream, fontSize: "0.9rem" }}>📍 {locationStr}</span>
          )}
        </div>
      )}

      {/* Client + contact */}
      {(booking.name || booking.company) && (
        <div style={{ color: BRAND.cream, fontSize: "0.9rem", marginBottom: "0.2rem" }}>
          👤 {booking.name}{booking.company ? ` · ${booking.company}` : ""}
        </div>
      )}
      {(booking.email || booking.phone) && (
        <div style={{ fontSize: "0.85rem", marginBottom: "0.5rem" }}>
          {booking.email && (
            <a href={`mailto:${booking.email}`} style={{ color: BRAND.sky, textDecoration: "none", marginRight: "0.8rem" }}>
              ✉ {booking.email}
            </a>
          )}
          {booking.phone && (
            <a href={`tel:${booking.phone}`} style={{ color: BRAND.sky, textDecoration: "none" }}>
              ☎ {booking.phone}
            </a>
          )}
        </div>
      )}

      {/* Stats row */}
      <div style={{
        display: "flex", flexWrap: "wrap", gap: "0.4rem",
        paddingTop: "0.55rem", borderTop: `1px dashed ${BRAND.line}`,
      }}>
        {rate && (
          <StatChip label="Rate" value={rate} accent={BRAND.gold} />
        )}
        {rate && (
          <StatChip
            label="Deposit"
            value={depositDone ? "✓ Paid" : "—"}
            accent={depositDone ? BRAND.green : BRAND.muted}
          />
        )}
        {booking.attendance != null && (
          <StatChip label="Guests" value={booking.attendance} />
        )}
        {booking.departureFrom === "travel" && (
          <StatChip label="Travel" value="Yes" accent={BRAND.orange} />
        )}
      </div>
    </div>
  );
}

function StatChip({ label, value, accent = BRAND.cream }) {
  return (
    <div style={{
      background: "rgba(0,0,0,0.25)",
      border: `1px solid ${BRAND.line}`,
      borderRadius: "8px",
      padding: "0.35rem 0.7rem",
      display: "flex", gap: "0.45rem", alignItems: "baseline",
    }}>
      <span style={{ color: BRAND.muted, fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.05em" }}>
        {label.toUpperCase()}
      </span>
      <span style={{ color: accent, fontWeight: 700, fontSize: "0.88rem" }}>
        {value}
      </span>
    </div>
  );
}

// ---------- Tab bar ----------

const EXTRA_TABS = [{ id: "tasks", label: "Tasks" }];

function TabBar({ active, onChange, taskCounts }) {
  const allTabs = [...TABS, ...EXTRA_TABS];
  return (
    <div style={{
      display: "flex",
      gap: "0.25rem",
      marginBottom: "0.85rem",
      overflowX: "auto",
      borderBottom: `1px solid ${BRAND.line}`,
      paddingBottom: "0.15rem",
    }}>
      {allTabs.map((t) => {
        const isActive = active === t.id;
        const pending = t.id === "tasks" ? (taskCounts?.pending || 0) : 0;
        const overdue = t.id === "tasks" ? (taskCounts?.overdue || 0) : 0;
        return (
          <button
            key={t.id}
            type="button"
            onClick={() => onChange(t.id)}
            style={{
              background: "transparent",
              color: isActive ? BRAND.gold : BRAND.muted,
              border: "none",
              borderBottom: isActive ? `2px solid ${BRAND.gold}` : "2px solid transparent",
              padding: "0.55rem 0.85rem",
              fontSize: "0.88rem",
              fontWeight: isActive ? 800 : 600,
              cursor: "pointer",
              whiteSpace: "nowrap",
              letterSpacing: "0.02em",
              display: "inline-flex",
              alignItems: "center",
              gap: "0.4rem",
            }}
          >
            {t.label}
            {overdue > 0 && <span style={{ background: "#c94444", color: "#fff", borderRadius: "999px", padding: "0.1rem 0.45rem", fontSize: "0.65rem", fontWeight: 800 }}>{overdue}</span>}
            {overdue === 0 && pending > 0 && <span style={{ background: "rgba(252,179,31,0.2)", color: BRAND.gold, borderRadius: "999px", padding: "0.1rem 0.45rem", fontSize: "0.65rem", fontWeight: 700 }}>{pending}</span>}
          </button>
        );
      })}
    </div>
  );
}

// ---------- main component ----------

export default function BookingDetail({ id, navigate }) {
  const { booking, loading, error, isNew } = useBooking(id);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [activeTab, setActiveTab] = useState(() => {
    try {
      const sp = new URLSearchParams(window.location.search);
      const hint = sp.get("tab");
      const valid = [...TABS.map((t) => t.id), ...EXTRA_TABS.map((t) => t.id)];
      if (hint && valid.includes(hint)) return hint;
    } catch {}
    return "details";
  });
  const [showQuickUpdate, setShowQuickUpdate] = useState(false);
  const [polishing, setPolishing] = useState(false);

  // Seed form when the canonical doc arrives / changes identity.
  useEffect(() => {
    if (booking) setForm(booking);
  }, [booking && booking.id, isNew]); // eslint-disable-line react-hooks/exhaustive-deps

  const dirty = useMemo(() => (form && booking ? isDirty(form, booking) : false), [form, booking]);

  if (loading || !form) {
    return (
      <div style={{ background: BRAND.bg, minHeight: "100vh", color: BRAND.muted, display: "flex", alignItems: "center", justifyContent: "center" }}>
        Loading booking…
      </div>
    );
  }

  if (error && !isNew) {
    return (
      <div style={{ background: BRAND.bg, minHeight: "100vh", color: BRAND.text, padding: "2rem" }}>
        <button onClick={() => navigate("/crm")} style={pillStyle}>← Bookings</button>
        <div style={{ marginTop: "1rem", color: BRAND.pink }}>Error: {error.message}</div>
      </div>
    );
  }

  const setField = (key) => (val) => setForm((f) => {
    const next = { ...f, [key]: val };
    // When confirmedDate changes, re-anchor any existing start/end times to
    // the new date — preserve H:M, swap the Y-M-D. Keeps the date and the
    // time-only inputs consistent without forcing the operator to re-pick.
    if (key === "confirmedDate" && val) {
      const anchor = tsToDate(val);
      if (anchor && !isNaN(anchor)) {
        for (const timeKey of ["confirmedStart", "confirmedEnd"]) {
          const t = tsToDate(f[timeKey]);
          if (t && !isNaN(t)) {
            const rebuilt = new Date(anchor);
            rebuilt.setHours(t.getHours(), t.getMinutes(), 0, 0);
            next[timeKey] = Timestamp.fromDate(rebuilt);
          }
        }
      }
    }
    return next;
  });

  // Inline single-field save for hero-card edits (e.g. event name). Mirrors
  // local form state AND writes through to Firestore immediately — bypasses
  // the main Save button so quick fixes don't require a tab dive.
  const saveFieldInline = async (key, value) => {
    setForm((f) => ({ ...f, [key]: value }));
    if (isNew || !booking?.id) return;
    await setDoc(
      doc(db, "bookings", booking.id),
      { [key]: value, updatedAt: serverTimestamp() },
      { merge: true },
    );
  };

  // ✨ Generate public listing — calls Claude Haiku on whatever internal data
  // exists and overwrites publicTitle + publicDescription. Per Dan's spec,
  // tap = replace.
  const generatePublicListing = async () => {
    if (polishing) return;
    setPolishing(true);
    try {
      const fn = httpsCallable(getFunctions(getApp()), "generatePublicListing");
      const res = await fn({ booking: form });
      setForm((f) => ({
        ...f,
        publicTitle:       res.data?.publicTitle       ?? f.publicTitle,
        publicDescription: res.data?.publicDescription ?? f.publicDescription,
      }));
    } catch (err) {
      alert("Generate failed: " + (err.message || "unknown"));
    } finally {
      setPolishing(false);
    }
  };

  const onSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      if (isNew) {
        const payload = {
          ...defaultBooking(),
          ...form,
          source: form.source || BOOKING_SOURCE.MANUAL,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          submittedAt: form.submittedAt || serverTimestamp(),
          statusChangedAt: serverTimestamp(),
        };
        delete payload.id;
        const ref = await addDoc(collection(db, "bookings"), payload);
        navigate(`/crm/bookings/${ref.id}`);
      } else {
        const diff = buildWritePayload(form, booking);
        if (booking.status !== form.status) diff.statusChangedAt = serverTimestamp();
        diff.updatedAt = serverTimestamp();
        await setDoc(doc(db, "bookings", booking.id), diff, { merge: true });
        setSavedFlash(true);
        setTimeout(() => setSavedFlash(false), 1500);
      }
    } catch (err) {
      console.error("save failed", err);
      alert("Save failed: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  const onToggleArchive = async () => {
    if (isNew) return;
    const next = form.archivedAt ? null : Timestamp.now();
    setForm((f) => ({ ...f, archivedAt: next }));
    try {
      await setDoc(doc(db, "bookings", booking.id), { archivedAt: next, updatedAt: serverTimestamp() }, { merge: true });
    } catch (err) {
      alert("Archive toggle failed: " + err.message);
    }
  };

  const onDelete = async () => {
    if (isNew) return;
    if (!window.confirm("Delete this booking permanently? This cannot be undone.")) return;
    try {
      await deleteDoc(doc(db, "bookings", booking.id));
      navigate("/crm");
    } catch (err) {
      alert("Delete failed: " + err.message);
    }
  };

  const onDeleteSeries = async () => {
    if (isNew || !form.seriesId) return;
    const name = form.publicTitle || form.eventName || "this series";
    if (!window.confirm(
      `Delete ALL upcoming occurrences of "${name}"?\n\n` +
      `Past occurrences and any with an open or closed shift will be kept. ` +
      `This cannot be undone.`
    )) return;
    try {
      const fn = httpsCallable(getFunctions(getApp()), "deleteEventSeries");
      const res = await fn({ seriesId: form.seriesId, onlyUpcoming: true });
      const { deleted = 0, keptShiftLinked = 0, keptPast = 0 } = res.data || {};
      alert(
        `Deleted ${deleted} upcoming booking${deleted === 1 ? "" : "s"}.\n` +
        (keptShiftLinked ? `Kept ${keptShiftLinked} with shift history.\n` : "") +
        (keptPast ? `Kept ${keptPast} past occurrence${keptPast === 1 ? "" : "s"}.` : "")
      );
      navigate("/crm");
    } catch (err) {
      alert("Series delete failed: " + (err.message || err));
    }
  };

  return (
    <div style={{
      background: BRAND.bg,
      minHeight: "100vh",
      color: BRAND.text,
      fontFamily: FONT.body,
      padding: "1rem",
      paddingBottom: "5rem",
      boxSizing: "border-box",
    }}>
      <div style={{ maxWidth: "820px", margin: "0 auto" }}>

        {/* Breadcrumb — ← Bookings pinned right for consistency. */}
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "0.8rem", flexWrap: "wrap" }}>
          {isNew && (
            <span style={{ color: BRAND.gold, fontWeight: 700, fontSize: "1rem" }}>
              New booking
            </span>
          )}
          {form.archivedAt && (
            <span style={{
              background: BRAND.panel, border: `1px solid ${BRAND.line}`, color: BRAND.muted,
              padding: "0.25rem 0.65rem", borderRadius: "999px", fontSize: "0.72rem", fontWeight: 700,
            }}>
              📦 Archived
            </span>
          )}
          <div style={{ flex: 1 }} />
          {!isNew && (
            <>
              <button type="button" onClick={onToggleArchive} title={form.archivedAt ? "Unarchive" : "Archive"} style={{ ...pillStyle, background: BRAND.panel, padding: "0.4rem 0.65rem" }}>
                {form.archivedAt ? "↩" : "📦"}
              </button>
              <button type="button" onClick={onDelete} title="Delete permanently" style={{ ...pillStyle, background: BRAND.panel, color: "#c94444", borderColor: "rgba(201,68,68,0.3)", padding: "0.4rem 0.65rem" }}>
                🗑
              </button>
              {form.seriesId && (
                <button
                  type="button"
                  onClick={onDeleteSeries}
                  title="Delete all upcoming occurrences in this series"
                  style={{ ...pillStyle, background: BRAND.panel, color: "#c94444", borderColor: "rgba(201,68,68,0.3)", padding: "0.4rem 0.65rem", fontWeight: 700 }}
                >
                  🗑× series
                </button>
              )}
            </>
          )}
          <button type="button" onClick={() => navigate("/crm")} style={pillStyle}>← Bookings</button>
        </div>

        {/* Hero card (read-only summary) */}
        <HeroCard booking={form} isNew={isNew} onSaveField={saveFieldInline} />

        {/* Pipeline strip — calendar, flash board, contract */}
        {!isNew && (
          <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap", marginBottom: "0.9rem", alignItems: "center" }}>
            {(() => {
              const calUrl = buildGoogleCalendarUrl(form);
              return calUrl ? (
                <a href={calUrl} target="_blank" rel="noopener noreferrer" style={{
                  background: BRAND.panel, border: `1px solid ${BRAND.blue}`, color: BRAND.blue,
                  borderRadius: "8px", padding: "0.45rem 0.7rem", fontSize: "0.8rem", fontWeight: 700,
                  textDecoration: "none", cursor: "pointer",
                }}>
                  📅 Add to my calendar
                </a>
              ) : (
                <span title="Set a confirmed date to enable" style={{
                  background: BRAND.panel, border: `1px dashed ${BRAND.line}`, color: BRAND.muted,
                  borderRadius: "8px", padding: "0.45rem 0.7rem", fontSize: "0.8rem", fontWeight: 600,
                }}>
                  📅 No date set
                </span>
              );
            })()}
            {form.isPublicEvent && (
              <AddToPublicCalendarButton bookingId={booking?.id} disabled={!form.confirmedDate && !form.confirmedStart} />
            )}
            <FlashBoardAttachChip booking={form} onAttach={saveFieldInline} />
            <FlashBoardChip booking={form} />
            <button
              type="button"
              onClick={() => navigate(`/crm/bookings/${booking.id}/contract`)}
              style={{
                background: BRAND.blue, border: "none", color: "white",
                borderRadius: "8px", padding: "0.45rem 0.7rem",
                fontSize: "0.8rem", fontWeight: 700, cursor: "pointer",
              }}
            >
              📜 Contract helper
            </button>
            {/* Link to the linked booth shift when one exists. Populated by
                ShiftManager's "Pre-fill from today's event" flow. */}
            {form.shiftId && (
              <button
                type="button"
                onClick={() => navigate(`/library?tab=shifts&shiftId=${form.shiftId}`)}
                title="View the linked booth shift in Library → Shifts"
                style={{
                  background: BRAND.panel, border: `1px solid ${BRAND.green}`, color: BRAND.green,
                  borderRadius: "8px", padding: "0.45rem 0.7rem",
                  fontSize: "0.8rem", fontWeight: 700, cursor: "pointer",
                }}
              >
                🥞 View shift →
              </button>
            )}
            {form.shiftId && (
              <a
                href={`/recap/${form.shiftId}`}
                target="_blank"
                rel="noopener noreferrer"
                title="Open the public per-shift recap page (deliverable to the client)"
                style={{
                  background: BRAND.panel, border: `1px solid ${BRAND.pink}`, color: BRAND.pink,
                  borderRadius: "8px", padding: "0.45rem 0.7rem",
                  fontSize: "0.8rem", fontWeight: 700, cursor: "pointer",
                  textDecoration: "none",
                }}
              >
                🎁 Event recap →
              </a>
            )}
            <button
              type="button"
              onClick={() => setShowQuickUpdate(true)}
              style={{
                background: BRAND.gold, border: "none", color: BRAND.bg,
                borderRadius: "8px", padding: "0.45rem 0.7rem",
                fontSize: "0.8rem", fontWeight: 800, cursor: "pointer",
              }}
            >
              ⚡ Quick update
            </button>
          </div>
        )}

        {showQuickUpdate && !isNew && (
          <QuickUpdateModal booking={form} onClose={() => setShowQuickUpdate(false)} />
        )}

        {!isNew && <ClientCard booking={form} onLink={saveFieldInline} navigate={navigate} />}
        {!isNew && booking && (
          <CorrespondencePanel
            booking={booking}
            onSent={(res) => {
              // Keep local form status in sync when the send auto-bumps
              // new_inquiry → contacted, so a later Save can't revert it.
              if (res?.statusBumped) setForm((f) => ({ ...f, status: "contacted" }));
            }}
          />
        )}
        {!isNew && <InvoicesPanel booking={form} />}
        {!isNew && <FlashConceptsPanel booking={form} />}
        {!isNew && <PreUploadsPanel bookingId={id} booking={form} />}

        {/* Tabbed sections */}
        <TabBar active={activeTab} onChange={setActiveTab} taskCounts={form._taskCounts} />
        {activeTab !== "tasks" && TABS.filter((t) => t.id === activeTab).map((tab) =>
          tab.sections.map(([sectionTitle, fields]) => (
            <SectionCard key={sectionTitle} title={sectionTitle}>
              {fields.map(([key, label, type]) => (
                <div key={key} style={{ gridColumn: fieldSpan(key, type) }}>
                  {type === "time" ? (
                    <TimeField
                      label={label}
                      value={form[key]}
                      onChange={setField(key)}
                      anchorDate={form.confirmedDate}
                    />
                  ) : (
                    <Field
                      label={label}
                      type={type}
                      value={form[key]}
                      onChange={setField(key)}
                    />
                  )}
                </div>
              ))}
            </SectionCard>
          ))
        )}

        {activeTab === "tasks" && !isNew && (
          <TasksPanel bookingId={id} tasks={booking?.tasks || form.tasks || []} />
        )}

        {/* ✨ Generate public listing — only shown on the Public tab. Drafts
            polished publicTitle + publicDescription from the booking's
            internal data. Tap = replace whatever was there before. */}
        {activeTab === "details" && (
          <div style={{
            marginTop: "0.75rem",
            padding: "0.85rem 1rem",
            background: "rgba(252,179,31,0.08)",
            border: `1px solid rgba(252,179,31,0.3)`,
            borderRadius: "10px",
            display: "flex", alignItems: "center", gap: "0.75rem", flexWrap: "wrap",
          }}>
            <div style={{ flex: 1, minWidth: "200px" }}>
              <div style={{ color: BRAND.gold, fontWeight: 700, fontSize: "0.88rem" }}>✨ Polish public copy with AI</div>
              <div style={{ color: BRAND.muted, fontSize: "0.78rem", marginTop: "2px", lineHeight: 1.4 }}>
                Drafts a public title + description from the internal details.
                Replaces what's there. Don't forget to <strong style={{ color: BRAND.cream }}>save</strong> after.
              </div>
            </div>
            <button onClick={generatePublicListing} disabled={polishing} style={{
              background: polishing ? "rgba(252,179,31,0.4)" : BRAND.gold,
              color: BRAND.dark,
              border: "none",
              borderRadius: "999px",
              padding: "0.5rem 1.1rem",
              fontWeight: 800,
              fontSize: "0.85rem",
              cursor: polishing ? "default" : "pointer",
              fontFamily: "inherit",
            }}>
              {polishing ? "Polishing…" : "✨ Generate"}
            </button>
          </div>
        )}

        {/* Archive/delete moved to breadcrumb header — always visible */}
      </div>

      {/* Sticky save bar */}
      <div style={{
        position: "fixed",
        bottom: 0, left: 0, right: 0,
        background: "rgba(26,10,18,0.96)",
        borderTop: `1px solid ${BRAND.line}`,
        padding: "0.6rem 1rem",
        display: "flex",
        alignItems: "center",
        gap: "0.5rem",
        justifyContent: "flex-end",
      }}>
        {savedFlash && <span style={{ color: BRAND.green, fontSize: "0.85rem", fontWeight: 700 }}>✓ Saved</span>}
        <button
          type="button"
          onClick={onSave}
          disabled={saving || (!isNew && !dirty)}
          style={{
            background: (!isNew && !dirty) ? BRAND.panel : BRAND.gold,
            color: (!isNew && !dirty) ? BRAND.muted : BRAND.bg,
            border: "none",
            borderRadius: "8px",
            padding: "0.65rem 1.2rem",
            fontWeight: 800,
            fontSize: "0.95rem",
            cursor: (saving || (!isNew && !dirty)) ? "default" : "pointer",
            letterSpacing: "0.02em",
          }}
        >
          {saving ? "Saving…" : isNew ? "Create booking" : (dirty ? "Save changes" : "No changes")}
        </button>
      </div>
    </div>
  );
}

const pillStyle = {
  background: BRAND.panel,
  color: BRAND.cream,
  border: `1px solid ${BRAND.line}`,
  borderRadius: "999px",
  padding: "0.4rem 0.85rem",
  cursor: "pointer",
  fontWeight: 600,
  fontSize: "0.85rem",
};

const INVOICE_STATUS_COLORS = {
  DRAFT:            { bg: "#234080", fg: "rgba(255,255,255,0.75)" },
  UNPAID:           { bg: "#fcb31f", fg: "#0d1833" },
  SCHEDULED:        { bg: "#4aa4e1", fg: "#0a1a2e" },
  PARTIALLY_PAID:   { bg: "#f57600", fg: "#ffffff" },
  PAID:             { bg: "#70be44", fg: "#ffffff" },
  CANCELED:         { bg: "#6b2234", fg: "rgba(255,255,255,0.7)" },
  PAYMENT_PENDING:  { bg: "#4aa4e1", fg: "#0a1a2e" },
  FAILED:           { bg: "#ef537e", fg: "#ffffff" },
  REFUNDED:         { bg: "#234080", fg: BRAND.cream },
};

function fmtInvoiceMoney(rate, pct) {
  const n = parseFloat(rate);
  if (!Number.isFinite(n)) return "—";
  const amount = n * pct;
  return "$" + amount.toLocaleString("en-US", { maximumFractionDigits: 2, minimumFractionDigits: amount % 1 === 0 ? 0 : 2 });
}

function InvoiceRow({ label, amount, status, url }) {
  const c = INVOICE_STATUS_COLORS[status] || INVOICE_STATUS_COLORS.UNPAID;
  return (
    <div style={{
      display: "grid",
      gridTemplateColumns: "auto 1fr auto",
      gap: "0.7rem",
      alignItems: "center",
      padding: "0.5rem 0",
      borderBottom: `1px dashed ${BRAND.line}`,
    }}>
      <div style={{ color: BRAND.cream, fontWeight: 700, fontSize: "0.9rem" }}>{label}</div>
      <div style={{ color: BRAND.gold, fontWeight: 700 }}>{amount}</div>
      <div style={{ display: "flex", gap: "0.4rem", alignItems: "center" }}>
        <span style={{
          background: c.bg, color: c.fg, borderRadius: "999px",
          padding: "0.2rem 0.55rem", fontSize: "0.7rem", fontWeight: 800, letterSpacing: "0.03em",
        }}>
          {status || "—"}
        </span>
        {url && (
          <a href={url} target="_blank" rel="noopener noreferrer" style={{
            color: BRAND.sky, fontSize: "0.78rem", fontWeight: 600, textDecoration: "none",
          }}>
            View →
          </a>
        )}
      </div>
    </div>
  );
}

// Phase order + timing — driven from the client side since the backend is one
// blocking call. The phases progress on a timer so the user always sees forward
// motion; whichever one is active when the call resolves gets cut short into
// success state. Phase durations are tuned to typical Square API latency.
const INVOICE_PHASES = [
  { id: "customer", label: "Looking up Square customer…", at: 0 },
  { id: "order",    label: "Creating order…", at: 1500 },
  { id: "invoice",  label: "Building payment schedule…", at: 3000 },
  { id: "publish",  label: "Publishing & sending email…", at: 5000 },
];

function InvoicesPanel({ booking }) {
  const [creating, setCreating] = useState(false);
  const [phase, setPhase] = useState(null); // INVOICE_PHASES[].id
  const [err, setErr] = useState(null);
  const [success, setSuccess] = useState(null); // { deposit, balance, email }
  // Link-existing-invoice flow (search the client's Square invoices).
  const [showLink, setShowLink] = useState(false);
  const [linkList, setLinkList] = useState(null); // null=not loaded, []=none found
  const [linkLoading, setLinkLoading] = useState(false);
  const [linkErr, setLinkErr] = useState(null);
  const [linkingId, setLinkingId] = useState(null);

  const hasNewInvoice = Boolean(booking.squareInvoiceId);
  const hasLegacyInvoices = Boolean(booking.squareDepositInvoiceId || booking.squareBalanceInvoiceId);
  const hasInvoices = hasNewInvoice || hasLegacyInvoices;

  const missing = [];
  if (!booking.totalRate || parseFloat(booking.totalRate) <= 0) missing.push("Total rate");
  if (!booking.name) missing.push("Client name");
  if (!booking.email) missing.push("Client email");

  // Auto-dismiss success card after a while so it doesn't linger forever
  useEffect(() => {
    if (!success) return;
    const t = setTimeout(() => setSuccess(null), 12000);
    return () => clearTimeout(t);
  }, [success]);

  const onCreate = async () => {
    if (creating) return;
    if (!window.confirm("Create + publish a single Square invoice? Deposit (50%) due immediately with weekly reminders until paid. Balance due on event date with daily reminders starting the day after. Client will receive ONE email.")) return;
    setCreating(true);
    setErr(null);
    setSuccess(null);
    setPhase("customer");

    const timers = INVOICE_PHASES.slice(1).map((p) =>
      setTimeout(() => setPhase(p.id), p.at)
    );

    try {
      const fn = httpsCallable(getFunctions(getApp()), "createBookingInvoices");
      const res = await fn({ bookingId: booking.id });
      setSuccess({
        email: booking.email,
        invoice: res.data?.invoice,
      });
    } catch (e) {
      setErr(e.message || "Invoice create failed");
    } finally {
      timers.forEach(clearTimeout);
      setCreating(false);
      setPhase(null);
    }
  };

  const openLink = async () => {
    setShowLink(true); setLinkErr(null); setLinkList(null); setLinkLoading(true);
    try {
      const fn = httpsCallable(getFunctions(getApp()), "listBookingSquareInvoices");
      const res = await fn({ bookingId: booking.id });
      setLinkList(res.data?.invoices || []);
    } catch (e) {
      setLinkErr(e.message || "Lookup failed");
      setLinkList([]);
    } finally {
      setLinkLoading(false);
    }
  };

  const doLink = async (invoiceId) => {
    if (linkingId) return;
    setLinkingId(invoiceId); setLinkErr(null);
    try {
      const fn = httpsCallable(getFunctions(getApp()), "linkExistingInvoice");
      await fn({ bookingId: booking.id, invoiceId });
      setShowLink(false); // booking onSnapshot refreshes → panel shows linked view
    } catch (e) {
      setLinkErr(e.message || "Link failed");
    } finally {
      setLinkingId(null);
    }
  };

  const phaseInfo = INVOICE_PHASES.find((p) => p.id === phase);
  const phaseIndex = phaseInfo ? INVOICE_PHASES.indexOf(phaseInfo) : -1;
  const progressPct = phaseIndex >= 0 ? ((phaseIndex + 1) / INVOICE_PHASES.length) * 100 : 0;

  return (
    <div style={{
      background: BRAND.panel,
      border: `1px solid ${BRAND.line}`,
      borderRadius: "10px",
      padding: "0.85rem 0.95rem",
      marginBottom: "0.9rem",
    }}>
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        gap: "0.5rem", marginBottom: hasInvoices ? "0.4rem" : 0,
      }}>
        <div style={{ color: BRAND.gold, fontWeight: 700, fontSize: "0.85rem", letterSpacing: "0.04em" }}>
          🧾 SQUARE INVOICES
        </div>
        {!hasInvoices && !success && (
          <div style={{ display: "flex", gap: "0.4rem", alignItems: "center" }}>
            <button
              type="button"
              onClick={openLink}
              disabled={creating || !booking.email}
              title={!booking.email ? "Client email required to find existing invoices" : "Link an invoice you already created in Square"}
              style={{
                background: BRAND.panel,
                color: (creating || !booking.email) ? BRAND.muted : BRAND.sky,
                border: `1px solid ${(creating || !booking.email) ? BRAND.line : BRAND.sky}`,
                borderRadius: "8px", padding: "0.45rem 0.85rem",
                fontWeight: 700, fontSize: "0.82rem",
                cursor: (creating || !booking.email) ? "default" : "pointer",
              }}
            >
              🔗 Link existing
            </button>
            <button
              type="button"
              onClick={onCreate}
              disabled={creating || missing.length > 0}
              title={missing.length > 0 ? `Missing: ${missing.join(", ")}` : ""}
              style={{
                background: (creating || missing.length > 0) ? BRAND.line : BRAND.green,
                color: (creating || missing.length > 0) ? BRAND.muted : "white",
                border: "none", borderRadius: "8px",
                padding: "0.45rem 0.85rem",
                fontWeight: 700, fontSize: "0.82rem",
                cursor: (creating || missing.length > 0) ? "default" : "pointer",
              }}
            >
              {creating ? "Working…" : "Create & publish"}
            </button>
          </div>
        )}
      </div>

      {/* Link existing invoice — search the client's Square invoices */}
      {showLink && !hasInvoices && (
        <div style={{
          background: BRAND.bg, border: `1px solid ${BRAND.sky}`,
          borderRadius: "8px", padding: "0.7rem 0.8rem", marginTop: "0.5rem",
        }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.5rem" }}>
            <div style={{ color: BRAND.sky, fontWeight: 700, fontSize: "0.82rem" }}>
              Link an existing Square invoice
            </div>
            <button type="button" onClick={() => setShowLink(false)} style={{
              background: "transparent", border: "none", color: BRAND.muted, cursor: "pointer", fontSize: "0.8rem",
            }}>✕</button>
          </div>
          {linkLoading && (
            <div style={{ color: BRAND.muted, fontSize: "0.82rem" }}>Searching this client's invoices…</div>
          )}
          {linkErr && (
            <div style={{ color: BRAND.pink, fontSize: "0.82rem", marginBottom: "0.4rem" }}>✗ {linkErr}</div>
          )}
          {!linkLoading && linkList && linkList.length === 0 && !linkErr && (
            <div style={{ color: BRAND.muted, fontSize: "0.82rem" }}>
              No Square invoices found for {booking.email}. (The invoice's recipient email must match this booking's email.)
            </div>
          )}
          {!linkLoading && linkList && linkList.map((inv) => (
            <div key={inv.id} style={{
              display: "flex", alignItems: "center", gap: "0.6rem", padding: "0.45rem 0",
              borderBottom: `1px dashed ${BRAND.line}`,
            }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ color: BRAND.cream, fontSize: "0.85rem", fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {inv.title || inv.number || "Invoice"}
                  {inv.amountCents != null && ` · $${(inv.amountCents / 100).toFixed(2)}`}
                </div>
                <div style={{ color: BRAND.muted, fontSize: "0.74rem" }}>
                  {inv.status || "—"}{inv.number ? ` · #${inv.number}` : ""}{inv.createdAt ? ` · ${new Date(inv.createdAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}` : ""}
                </div>
              </div>
              <button
                type="button"
                onClick={() => doLink(inv.id)}
                disabled={Boolean(linkingId)}
                style={{
                  background: linkingId === inv.id ? BRAND.line : BRAND.sky,
                  color: linkingId === inv.id ? BRAND.muted : "white",
                  border: "none", borderRadius: "8px", padding: "0.35rem 0.7rem",
                  fontSize: "0.78rem", fontWeight: 700, cursor: linkingId ? "default" : "pointer", flexShrink: 0,
                }}
              >
                {linkingId === inv.id ? "Linking…" : "Link"}
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Progress while creating */}
      {creating && (
        <div style={{ marginTop: "0.5rem" }}>
          <div style={{
            color: BRAND.cream, fontSize: "0.85rem", fontWeight: 600, marginBottom: "0.4rem",
            display: "flex", alignItems: "center", gap: "0.5rem",
          }}>
            <span style={{
              display: "inline-block", width: "12px", height: "12px",
              border: `2px solid ${BRAND.line}`, borderTopColor: BRAND.gold,
              borderRadius: "50%", animation: "jopSpin 0.8s linear infinite",
            }} />
            {phaseInfo?.label || "Working…"}
          </div>
          <div style={{
            background: BRAND.line, height: "5px", borderRadius: "999px", overflow: "hidden",
          }}>
            <div style={{
              width: `${progressPct}%`,
              height: "100%",
              background: `linear-gradient(90deg, ${BRAND.gold}, ${BRAND.green})`,
              transition: "width 0.6s ease",
            }} />
          </div>
        </div>
      )}

      {err && (
        <div style={{
          background: "rgba(201,68,68,0.15)", border: `1px solid ${BRAND.red}`,
          color: BRAND.pink, fontSize: "0.85rem", padding: "0.55rem 0.7rem",
          borderRadius: "8px", marginTop: "0.5rem",
        }}>
          ✗ {err}
        </div>
      )}

      {success && success.invoice && (
        <div style={{
          background: "rgba(112,190,68,0.15)", border: `1px solid ${BRAND.green}`,
          borderRadius: "8px", padding: "0.7rem 0.85rem", marginTop: "0.5rem",
        }}>
          <div style={{ color: BRAND.green, fontWeight: 800, fontSize: "0.95rem", marginBottom: "0.4rem" }}>
            ✓ Invoice published &amp; emailed to {success.email}
          </div>
          <div style={{ color: BRAND.cream, fontSize: "0.82rem", lineHeight: 1.5 }}>
            <div>Total: <strong>${(success.invoice.totalCents / 100).toFixed(2)}</strong></div>
            <div>· Deposit (50%): ${(success.invoice.depositCents / 100).toFixed(2)} due {success.invoice.depositDue}</div>
            <div>· Balance: ${(success.invoice.balanceCents / 100).toFixed(2)} due {success.invoice.balanceDue}</div>
            {success.invoice.url && (
              <div style={{ marginTop: "0.3rem" }}>
                <a href={success.invoice.url} target="_blank" rel="noopener noreferrer" style={{ color: BRAND.sky, fontWeight: 600 }}>
                  View invoice in Square ↗
                </a>
              </div>
            )}
          </div>
          <div style={{ color: BRAND.muted, fontSize: "0.72rem", marginTop: "0.45rem" }}>
            Square will auto-send reminders before each payment is due.
          </div>
        </div>
      )}

      {!hasInvoices && !creating && !success && missing.length > 0 && (
        <div style={{ color: BRAND.muted, fontSize: "0.78rem", marginTop: "0.3rem" }}>
          Needs: {missing.join(", ")}
        </div>
      )}

      {!hasInvoices && !creating && !success && missing.length === 0 && !err && (
        <div style={{ color: BRAND.muted, fontSize: "0.78rem", marginTop: "0.3rem" }}>
          Deposit (50%) + balance (50%) will be published and emailed to the client.
        </div>
      )}

      {hasNewInvoice && (
        <>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0.4rem 0", borderBottom: `1px dashed ${BRAND.line}` }}>
            <div>
              <div style={{ color: BRAND.cream, fontWeight: 700, fontSize: "0.92rem" }}>
                Payment schedule · {fmtInvoiceMoney(booking.totalRate, 1)}
              </div>
              <div style={{ color: BRAND.muted, fontSize: "0.75rem", marginTop: "0.2rem" }}>
                One invoice with deposit + balance scheduled. Square sends reminders.
              </div>
            </div>
            <div style={{ display: "flex", gap: "0.4rem", alignItems: "center" }}>
              <span style={{
                background: (INVOICE_STATUS_COLORS[booking.squareInvoiceStatus] || INVOICE_STATUS_COLORS.UNPAID).bg,
                color: (INVOICE_STATUS_COLORS[booking.squareInvoiceStatus] || INVOICE_STATUS_COLORS.UNPAID).fg,
                borderRadius: "999px", padding: "0.2rem 0.55rem", fontSize: "0.7rem", fontWeight: 800, letterSpacing: "0.03em",
              }}>
                {booking.squareInvoiceStatus || "—"}
              </span>
              {booking.squareInvoiceUrl && (
                <a href={booking.squareInvoiceUrl} target="_blank" rel="noopener noreferrer" style={{ color: BRAND.sky, fontSize: "0.78rem", fontWeight: 600, textDecoration: "none" }}>
                  View ↗
                </a>
              )}
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.5rem", padding: "0.5rem 0 0.2rem", fontSize: "0.82rem" }}>
            <div>
              <div style={{ color: BRAND.muted, fontSize: "0.7rem", letterSpacing: "0.03em" }}>DEPOSIT (50%)</div>
              <div style={{ color: booking.depositPaidAt ? BRAND.green : BRAND.cream, fontWeight: 700 }}>
                {fmtInvoiceMoney(booking.totalRate, 0.5)} {booking.depositPaidAt ? "· ✓ Paid" : ""}
              </div>
            </div>
            <div>
              <div style={{ color: BRAND.muted, fontSize: "0.7rem", letterSpacing: "0.03em" }}>BALANCE</div>
              <div style={{ color: booking.balancePaidAt ? BRAND.green : BRAND.cream, fontWeight: 700 }}>
                {fmtInvoiceMoney(booking.totalRate, 0.5)} {booking.balancePaidAt ? "· ✓ Paid" : ""}
              </div>
            </div>
          </div>
          <div style={{ color: BRAND.muted, fontSize: "0.72rem", marginTop: "0.4rem" }}>
            Status polls every 5 minutes. Deposit paid → booking auto-advances to "Deposit Received".
          </div>
        </>
      )}

      {hasLegacyInvoices && !hasNewInvoice && (
        <>
          <InvoiceRow
            label="Deposit (50%) [legacy]"
            amount={fmtInvoiceMoney(booking.totalRate, 0.5)}
            status={booking.squareDepositStatus}
            url={booking.squareDepositUrl}
          />
          <InvoiceRow
            label="Balance (50%) [legacy]"
            amount={fmtInvoiceMoney(booking.totalRate, 0.5)}
            status={booking.squareBalanceStatus}
            url={booking.squareBalanceUrl}
          />
          <div style={{ color: BRAND.muted, fontSize: "0.72rem", marginTop: "0.4rem" }}>
            Pre-migration two-invoice format. Status still polls every 5 minutes.
          </div>
        </>
      )}
    </div>
  );
}

function FlashConceptsPanel({ booking }) {
  const subs = Array.isArray(booking.flashSubmissions) ? booking.flashSubmissions : [];
  if (subs.length === 0) return null;

  const uploadUrl = booking.flashToken
    ? `https://thejoyofpancakes.com/flash/${booking.flashToken}`
    : null;

  const copyLink = async () => {
    if (!uploadUrl) return;
    try { await navigator.clipboard.writeText(uploadUrl); } catch { /* ignore */ }
  };

  return (
    <div style={{
      background: BRAND.panel,
      border: `1px solid ${BRAND.line}`,
      borderRadius: "10px",
      padding: "0.85rem 0.95rem",
      marginBottom: "0.9rem",
    }}>
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        marginBottom: "0.5rem",
      }}>
        <div style={{ color: BRAND.gold, fontWeight: 700, fontSize: "0.85rem", letterSpacing: "0.04em" }}>
          🎨 FLASH BOARD CONCEPTS · {subs.length}
        </div>
        {uploadUrl && (
          <button
            type="button"
            onClick={copyLink}
            style={{
              background: "transparent",
              border: `1px solid ${BRAND.line}`,
              color: BRAND.cream,
              borderRadius: "8px",
              padding: "0.3rem 0.6rem",
              fontSize: "0.75rem",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            📋 Copy upload link
          </button>
        )}
      </div>

      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
        gap: "0.6rem",
      }}>
        {subs.map((s, i) => {
          const when = s.submittedAt?.toDate ? s.submittedAt.toDate() : null;
          return (
            <div key={i} style={{
              background: BRAND.bg,
              border: `1px solid ${BRAND.line}`,
              borderRadius: "8px",
              overflow: "hidden",
            }}>
              <a href={s.imageUrl} target="_blank" rel="noopener noreferrer">
                <img
                  src={s.imageUrl}
                  alt={`Concept ${i + 1}`}
                  style={{
                    display: "block",
                    width: "100%",
                    aspectRatio: "1",
                    objectFit: "cover",
                    background: "#000",
                  }}
                />
              </a>
              <div style={{ padding: "0.5rem 0.6rem" }}>
                {s.notes && (
                  <div style={{ color: BRAND.cream, fontSize: "0.82rem", lineHeight: 1.4, marginBottom: "0.3rem", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                    {s.notes}
                  </div>
                )}
                {when && (
                  <div style={{ color: BRAND.muted, fontSize: "0.7rem" }}>
                    {when.toLocaleDateString("en-US", { month: "short", day: "numeric" })} · {when.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── PRE-UPLOAD ORDERS PREVIEW ───────────────────────────────────────────────
// Read-only list of client-pre-uploaded orders bound to this booking.
// Surfaces what's been staged before the event so Sarah-the-client's
// curation can be reviewed. Status badges distinguish staged (waiting to
// be promoted at the booth) from promoted (already pulled into a shift)
// and trashed (recoverable from the trash bin). The "Send pre-upload
// link" action will live here in step 4.
function PreUploadsPanel({ bookingId, booking }) {
  const [rows, setRows] = useState([]);
  const [token, setToken] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [copied, setCopied] = useState(false);

  // Generate-link form state
  const [showCreate, setShowCreate]   = useState(false);
  const [maxUploads, setMaxUploads]   = useState(10);
  const [recipientEmail, setRecipientEmail] = useState("");
  const [customMessage, setCustomMessage]   = useState("");
  const [busy, setBusy] = useState(null); // "create" | "send" | "revoke"
  const [actionResult, setActionResult] = useState(null);

  // Pre-fill recipient email when the booking loads (or changes) — only if
  // the staffer hasn't started typing their own value yet.
  useEffect(() => {
    if (booking?.email && !recipientEmail) setRecipientEmail(booking.email);
  }, [booking?.email]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!bookingId) { setRows([]); setLoaded(true); return; }
    const q = query(
      collection(db, "orders"),
      where("bookingId", "==", bookingId),
      where("isPreUpload", "==", true),
    );
    const unsub = onSnapshot(q, (snap) => {
      const list = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (a.stagedAt?.toMillis?.() ?? 0) - (b.stagedAt?.toMillis?.() ?? 0));
      setRows(list);
      setLoaded(true);
    }, () => setLoaded(true));
    return () => unsub();
  }, [bookingId]);

  // Token subscription — surfaces the /upload link in the natural CRM home
  // even before any uploads exist. Most recent token wins if there are
  // somehow more than one (shouldn't happen — staff revokes + reissues).
  useEffect(() => {
    if (!bookingId) { setToken(null); return; }
    const q = query(
      collection(db, "tokens"),
      where("source", "==", "preupload"),
      where("bookingId", "==", bookingId),
    );
    const unsub = onSnapshot(q, (snap) => {
      const tokens = snap.docs
        .map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (b.createdAt?.toMillis?.() ?? 0) - (a.createdAt?.toMillis?.() ?? 0));
      setToken(tokens[0] || null);
    });
    return () => unsub();
  }, [bookingId]);

  if (!loaded) return null;

  const stagedCount   = rows.filter(r => r.state === "staged" && !r.trashedAt).length;
  const promotedCount = rows.filter(r => r.state !== "staged" && !r.trashedAt).length;
  const trashedCount  = rows.filter(r => r.trashedAt).length;

  async function handleCreateAndSend() {
    setActionResult(null);
    const cap = Math.max(1, Math.min(200, Number(maxUploads) || 10));
    if (!recipientEmail.includes("@")) {
      setActionResult({ kind: "error", msg: "Enter a valid recipient email." });
      return;
    }
    setBusy("create");
    try {
      const fnCreate = httpsCallable(getFunctions(getApp()), "createPreUploadToken");
      const { data: created } = await fnCreate({ bookingId, maxUploads: cap });
      const fnSend = httpsCallable(getFunctions(getApp()), "sendPreUploadLink");
      await fnSend({ tokenId: created.tokenId, toEmail: recipientEmail.trim(), customMessage: customMessage.trim() || null });
      setActionResult({ kind: "sent", to: recipientEmail.trim() });
      setShowCreate(false);
      setCustomMessage("");
    } catch (err) {
      setActionResult({ kind: "error", msg: err?.message || "Failed to generate and send link." });
    } finally {
      setBusy(null);
    }
  }

  async function handleResend() {
    if (!token) return;
    if (!recipientEmail.includes("@")) {
      setActionResult({ kind: "error", msg: "Enter a valid recipient email." });
      return;
    }
    setActionResult(null);
    setBusy("send");
    try {
      const fnSend = httpsCallable(getFunctions(getApp()), "sendPreUploadLink");
      await fnSend({ tokenId: token.id, toEmail: recipientEmail.trim(), customMessage: customMessage.trim() || null });
      setActionResult({ kind: "sent", to: recipientEmail.trim() });
      setCustomMessage("");
    } catch (err) {
      setActionResult({ kind: "error", msg: err?.message || "Failed to send." });
    } finally {
      setBusy(null);
    }
  }

  async function handleRevoke() {
    if (!token) return;
    if (!window.confirm("Revoke this upload link? Existing uploads stay (staff can still promote them) but the client can no longer submit new ones. You can generate a fresh link after.")) return;
    setBusy("revoke");
    setActionResult(null);
    try {
      const fn = httpsCallable(getFunctions(getApp()), "revokePreUploadToken");
      await fn({ tokenId: token.id });
      setActionResult({ kind: "revoked" });
    } catch (err) {
      setActionResult({ kind: "error", msg: err?.message || "Revoke failed." });
    } finally {
      setBusy(null);
    }
  }

  return (
    <div style={{
      background: BRAND.panel,
      border: `1px solid ${BRAND.line}`,
      borderRadius: "10px",
      padding: "0.85rem 0.95rem",
      marginBottom: "0.9rem",
    }}>
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        marginBottom: "0.6rem", flexWrap: "wrap", gap: "0.5rem",
      }}>
        <div style={{ color: BRAND.gold, fontWeight: 700, fontSize: "0.85rem", letterSpacing: "0.04em" }}>
          📥 PRE-UPLOADED DESIGNS{rows.length > 0 ? ` · ${rows.length}` : ""}
        </div>
        {rows.length > 0 && (
          <div style={{ color: BRAND.muted, fontSize: "0.7rem" }}>
            {stagedCount > 0   && <span style={{ marginRight: "0.6rem" }}>🟡 {stagedCount} staged</span>}
            {promotedCount > 0 && <span style={{ marginRight: "0.6rem", color: BRAND.green }}>✓ {promotedCount} promoted</span>}
            {trashedCount > 0  && <span style={{ color: BRAND.red }}>🗑 {trashedCount} trashed</span>}
          </div>
        )}
      </div>

      {token && token.status !== "revoked" && (() => {
        const url = `${window.location.origin}/upload?t=${token.id}`;
        const statusBadge = token.status === "active"
          ? { color: BRAND.green, label: "active" }
          : token.status === "exhausted"
            ? { color: BRAND.gold, label: "cap reached" }
            : token.status === "revoked"
              ? { color: BRAND.red, label: "revoked" }
              : { color: BRAND.muted, label: token.status };
        return (
          <div style={{
            background: BRAND.bg, border: `1px solid ${BRAND.line}`, borderRadius: "8px",
            padding: "0.6rem 0.75rem", marginBottom: "0.75rem", display: "flex",
            flexDirection: "column", gap: "0.45rem",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
              <span style={{ color: BRAND.cream, fontSize: "0.74rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em" }}>Client upload link</span>
              <span style={{ color: statusBadge.color, fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em" }}>● {statusBadge.label}</span>
              <span style={{ color: BRAND.muted, fontSize: "0.7rem" }}>
                {token.usedCount ?? 0} of {token.maxUploads} used
              </span>
            </div>
            <div style={{
              background: BRAND.panel, color: BRAND.sky, fontSize: "0.74rem",
              fontFamily: "monospace", padding: "0.4rem 0.55rem", borderRadius: "6px",
              border: `1px solid ${BRAND.line}`, wordBreak: "break-all", lineHeight: 1.4,
            }}>
              {url}
            </div>
            <div style={{ display: "flex", gap: "0.45rem", flexWrap: "wrap", alignItems: "center" }}>
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard.writeText(url).then(() => {
                    setCopied(true);
                    setTimeout(() => setCopied(false), 1800);
                  });
                }}
                style={{
                  background: copied ? BRAND.green : BRAND.panel,
                  color: copied ? "white" : BRAND.cream,
                  border: `1px solid ${copied ? BRAND.green : BRAND.line}`,
                  borderRadius: "6px", padding: "0.4rem 0.7rem",
                  fontSize: "0.76rem", fontWeight: 700, cursor: "pointer",
                }}
              >
                {copied ? "✓ Copied" : "📋 Copy link"}
              </button>
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  background: BRAND.panel, color: BRAND.gold,
                  border: `1px solid ${BRAND.line}`, borderRadius: "6px",
                  padding: "0.4rem 0.7rem", fontSize: "0.76rem", fontWeight: 700,
                  textDecoration: "none",
                }}
              >
                ↗ Open page
              </a>
              {token.status !== "revoked" && (
                <button
                  type="button"
                  onClick={handleRevoke}
                  disabled={busy !== null}
                  style={{
                    background: BRAND.panel, color: BRAND.red,
                    border: `1px solid ${BRAND.line}`, borderRadius: "6px",
                    padding: "0.4rem 0.7rem", fontSize: "0.76rem", fontWeight: 700,
                    cursor: busy ? "default" : "pointer", marginLeft: "auto",
                  }}
                >
                  {busy === "revoke" ? "…" : "🚫 Revoke"}
                </button>
              )}
            </div>
            {/* Resend-to row: email field + send button. Pre-filled from
                booking.email; staff can override. lastSentTo/At surfaced
                below the field if we've sent before. */}
            {token.status !== "revoked" && (
              <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem", marginTop: "0.3rem", paddingTop: "0.5rem", borderTop: `1px solid ${BRAND.line}` }}>
                <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap", alignItems: "center" }}>
                  <input
                    type="email"
                    value={recipientEmail}
                    onChange={(e) => setRecipientEmail(e.target.value)}
                    placeholder="client@example.com"
                    style={{
                      flex: 1, minWidth: 180, padding: "0.4rem 0.55rem",
                      background: BRAND.panel, color: BRAND.cream,
                      border: `1px solid ${BRAND.line}`, borderRadius: "6px",
                      fontSize: "0.78rem",
                    }}
                  />
                  <button
                    type="button"
                    onClick={handleResend}
                    disabled={busy !== null || !recipientEmail.includes("@")}
                    style={{
                      background: BRAND.gold, color: BRAND.bg, border: "none",
                      borderRadius: "6px", padding: "0.45rem 0.85rem",
                      fontSize: "0.78rem", fontWeight: 800, cursor: busy ? "default" : "pointer",
                      opacity: !recipientEmail.includes("@") ? 0.5 : 1,
                    }}
                  >
                    {busy === "send" ? "Sending…" : "📧 Send link"}
                  </button>
                </div>
                <textarea
                  value={customMessage}
                  onChange={(e) => setCustomMessage(e.target.value)}
                  placeholder="Optional note to include in the email (e.g. 'looking forward to working with you')"
                  rows={2}
                  maxLength={1000}
                  style={{
                    width: "100%", padding: "0.4rem 0.55rem", boxSizing: "border-box",
                    background: BRAND.panel, color: BRAND.cream,
                    border: `1px solid ${BRAND.line}`, borderRadius: "6px",
                    fontSize: "0.76rem", resize: "vertical", fontFamily: "inherit",
                  }}
                />
                {token.lastSentAt && (
                  <div style={{ color: BRAND.muted, fontSize: "0.68rem" }}>
                    Last sent to <span style={{ color: BRAND.cream }}>{token.lastSentTo}</span> on{" "}
                    {token.lastSentAt?.toDate ? token.lastSentAt.toDate().toLocaleString() : ""}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })()}

      {/* Previous-link-revoked notice — small audit trail before the Generate form below. */}
      {token && token.status === "revoked" && (
        <div style={{
          background: "rgba(201,68,68,0.08)", border: `1px solid ${BRAND.red}`, borderRadius: "8px",
          padding: "0.5rem 0.7rem", marginBottom: "0.6rem", color: BRAND.red, fontSize: "0.74rem",
        }}>
          🚫 Previous link revoked{token.lastTouchedAt?.toDate ? ` on ${token.lastTouchedAt.toDate().toLocaleString()}` : ""}. Generate a new link below if you still want the client to be able to upload.
        </div>
      )}

      {/* No active token — show "Generate link" CTA + inline form. Renders
          both when no token has ever existed AND when the latest token was
          revoked (so staff can mint a fresh one). */}
      {(!token || token.status === "revoked") && (
        <div style={{
          background: BRAND.bg, border: `1px dashed ${BRAND.line}`, borderRadius: "8px",
          padding: "0.7rem 0.85rem", marginBottom: "0.75rem",
        }}>
          {!showCreate ? (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.5rem", flexWrap: "wrap" }}>
              <div style={{ color: BRAND.muted, fontSize: "0.78rem", lineHeight: 1.45 }}>
                {token?.status === "revoked"
                  ? "Generate a fresh upload link to replace the revoked one."
                  : "No upload link yet. Generate one to let the client stage custom designs ahead of the event."}
              </div>
              <button
                type="button"
                onClick={() => setShowCreate(true)}
                style={{
                  background: BRAND.gold, color: BRAND.bg, border: "none",
                  borderRadius: "6px", padding: "0.5rem 0.85rem",
                  fontSize: "0.78rem", fontWeight: 800, cursor: "pointer",
                }}
              >
                ✨ Generate upload link
              </button>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
              <div style={{ color: BRAND.cream, fontSize: "0.78rem", fontWeight: 700 }}>New upload link</div>
              <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", alignItems: "center" }}>
                <label style={{ color: BRAND.muted, fontSize: "0.72rem", display: "flex", alignItems: "center", gap: "0.35rem" }}>
                  Max uploads
                  <input
                    type="number"
                    min={1}
                    max={200}
                    value={maxUploads}
                    onChange={(e) => setMaxUploads(e.target.value)}
                    style={{
                      width: 64, padding: "0.35rem 0.5rem", background: BRAND.panel, color: BRAND.cream,
                      border: `1px solid ${BRAND.line}`, borderRadius: "6px", fontSize: "0.78rem",
                    }}
                  />
                </label>
                <input
                  type="email"
                  value={recipientEmail}
                  onChange={(e) => setRecipientEmail(e.target.value)}
                  placeholder="client@example.com"
                  style={{
                    flex: 1, minWidth: 200, padding: "0.4rem 0.55rem",
                    background: BRAND.panel, color: BRAND.cream,
                    border: `1px solid ${BRAND.line}`, borderRadius: "6px",
                    fontSize: "0.78rem",
                  }}
                />
              </div>
              <textarea
                value={customMessage}
                onChange={(e) => setCustomMessage(e.target.value)}
                placeholder="Optional note (e.g. 'looking forward to your event')"
                rows={2}
                maxLength={1000}
                style={{
                  width: "100%", padding: "0.4rem 0.55rem", boxSizing: "border-box",
                  background: BRAND.panel, color: BRAND.cream,
                  border: `1px solid ${BRAND.line}`, borderRadius: "6px",
                  fontSize: "0.76rem", resize: "vertical", fontFamily: "inherit",
                }}
              />
              <div style={{ display: "flex", gap: "0.45rem", flexWrap: "wrap" }}>
                <button
                  type="button"
                  onClick={handleCreateAndSend}
                  disabled={busy !== null || !recipientEmail.includes("@")}
                  style={{
                    background: BRAND.gold, color: BRAND.bg, border: "none",
                    borderRadius: "6px", padding: "0.5rem 1rem",
                    fontSize: "0.78rem", fontWeight: 800, cursor: busy ? "default" : "pointer",
                    opacity: !recipientEmail.includes("@") ? 0.5 : 1,
                  }}
                >
                  {busy === "create" ? "Sending…" : "✨ Generate & send"}
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreate(false)}
                  disabled={busy !== null}
                  style={{
                    background: "transparent", color: BRAND.muted,
                    border: `1px solid ${BRAND.line}`, borderRadius: "6px",
                    padding: "0.5rem 0.75rem", fontSize: "0.78rem", fontWeight: 700,
                    cursor: busy ? "default" : "pointer",
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {actionResult?.kind === "sent" && (
        <div style={{ background: "rgba(112,190,68,0.1)", border: `1px solid ${BRAND.green}`, borderRadius: "6px", padding: "0.5rem 0.7rem", marginBottom: "0.75rem", color: BRAND.green, fontSize: "0.76rem" }}>
          ✓ Sent to {actionResult.to}
        </div>
      )}
      {actionResult?.kind === "revoked" && (
        <div style={{ background: "rgba(239,83,126,0.1)", border: `1px solid ${BRAND.red}`, borderRadius: "6px", padding: "0.5rem 0.7rem", marginBottom: "0.75rem", color: BRAND.red, fontSize: "0.76rem" }}>
          🚫 Link revoked
        </div>
      )}
      {actionResult?.kind === "error" && (
        <div style={{ background: "rgba(239,83,126,0.1)", border: `1px solid ${BRAND.red}`, borderRadius: "6px", padding: "0.5rem 0.7rem", marginBottom: "0.75rem", color: BRAND.red, fontSize: "0.76rem" }}>
          ⚠ {actionResult.msg}
        </div>
      )}

      {rows.length > 0 && (
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))",
        gap: "0.6rem",
      }}>
        {rows.map((o) => {
          const thumb = o.photo_cutout_url || o.croppedImageUrl || o.imageUrl || o.photo_url;
          const name  = o.recipientName || o.subjectName || o.customerName || "Unnamed";
          const design = o.designLabel
            || (o.orderType === "freestyle_flash" || o.isFreestyle ? "Freestyle" : null)
            || (o.orderType === "custom" ? "Custom" : null)
            || o.designId
            || "—";
          const when = o.stagedAt?.toDate ? o.stagedAt.toDate() : null;
          const isTrashed   = !!o.trashedAt;
          const isPromoted  = !isTrashed && o.state !== "staged";
          const badgeColor  = isTrashed ? BRAND.red : isPromoted ? BRAND.green : BRAND.gold;
          const badgeText   = isTrashed ? "trashed" : isPromoted ? "promoted" : "staged";
          return (
            <div key={o.id} style={{
              background: BRAND.bg,
              border: `1px solid ${BRAND.line}`,
              borderRadius: "8px",
              overflow: "hidden",
              opacity: isTrashed ? 0.55 : 1,
            }}>
              {thumb ? (
                <img src={thumb} alt={name} style={{ display: "block", width: "100%", aspectRatio: "1", objectFit: "cover", background: "#000" }} />
              ) : (
                <div style={{ width: "100%", aspectRatio: "1", background: "#000", color: BRAND.muted, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.4rem" }}>📥</div>
              )}
              <div style={{ padding: "0.5rem 0.6rem" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.4rem" }}>
                  <div style={{ color: BRAND.cream, fontWeight: 700, fontSize: "0.85rem", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name}</div>
                  {o.holdIndefinitely && <span title="Held for rescheduled event" style={{ fontSize: "0.7rem" }}>🔒</span>}
                </div>
                <div style={{ color: BRAND.muted, fontSize: "0.72rem", marginTop: "1px" }}>{design}</div>
                <div style={{ display: "flex", alignItems: "center", gap: "0.4rem", marginTop: "0.3rem" }}>
                  <span style={{ color: badgeColor, fontSize: "0.66rem", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 700 }}>● {badgeText}</span>
                  {when && (
                    <span style={{ color: BRAND.muted, fontSize: "0.66rem" }}>
                      · {when.toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      )}
    </div>
  );
}

function AddToPublicCalendarButton({ bookingId, disabled }) {
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState(null);

  const onClick = async () => {
    if (busy || done) return;
    setBusy(true);
    setErr(null);
    try {
      const fn = httpsCallable(getFunctions(getApp()), "addToPublicCalendar");
      await fn({ bookingId });
      setDone(true);
    } catch (e) {
      setErr(e.message || "Failed");
    } finally {
      setBusy(false);
    }
  };

  if (done) return (
    <span style={{
      background: BRAND.panel, border: `1px solid ${BRAND.green}`, color: BRAND.green,
      borderRadius: "8px", padding: "0.45rem 0.7rem", fontSize: "0.8rem", fontWeight: 700,
    }}>
      📅 Added to public calendar
    </span>
  );

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || busy}
      title={disabled ? "Set a confirmed date first" : "Create event on public Google Calendar"}
      style={{
        background: BRAND.panel, border: `1px solid ${BRAND.orange}`, color: BRAND.orange,
        borderRadius: "8px", padding: "0.45rem 0.7rem", fontSize: "0.8rem", fontWeight: 700,
        cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.5 : 1,
      }}
    >
      {busy ? "Adding..." : "📅 Add to public calendar"}
      {err && <span style={{ color: BRAND.red, marginLeft: "0.3rem", fontSize: "0.7rem" }}>{err}</span>}
    </button>
  );
}

function FlashBoardChip({ booking }) {
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState(null);

  const eligible = booking.flashBoard === true && booking.email;
  const sentAt = booking.flashEmailSentAt;
  const sent = Boolean(sentAt);

  const onSend = async () => {
    if (sending) return;
    if (sent && !window.confirm("Resend the flash board email to this client?")) return;
    setSending(true); setErr(null);
    try {
      const fn = httpsCallable(getFunctions(getApp()), "triggerFlashBoardPipeline");
      await fn({ bookingId: booking.id });
    } catch (e) {
      setErr(e.message || "Send failed");
    } finally {
      setSending(false);
    }
  };

  if (!eligible) {
    const reason = !booking.flashBoard ? "Toggle 'Flash board' on in Logistics first" : "Client email required";
    return (
      <span title={reason} style={{
        background: BRAND.panel,
        border: `1px dashed ${BRAND.line}`,
        color: BRAND.muted,
        borderRadius: "8px",
        padding: "0.45rem 0.7rem",
        fontSize: "0.8rem",
        fontWeight: 600,
        cursor: "help",
      }}>
        🎨 Flash board email
      </span>
    );
  }

  const sentDate = sentAt?.toDate ? sentAt.toDate() : (sentAt instanceof Date ? sentAt : null);
  const sentDisplay = sentDate ? sentDate.toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "";

  return (
    <button
      type="button"
      onClick={onSend}
      disabled={sending}
      title={err || (sent ? `Sent ${sentDisplay} — click to resend` : "Send flash board email to client")}
      style={{
        background: sent ? BRAND.panel : BRAND.pink,
        border: sent ? `1px solid ${BRAND.green}` : "none",
        color: sent ? BRAND.green : "white",
        borderRadius: "8px",
        padding: "0.45rem 0.7rem",
        fontSize: "0.8rem",
        fontWeight: 700,
        cursor: sending ? "default" : "pointer",
      }}
    >
      {sending ? "Sending…" : sent ? `🎨 Sent ${sentDisplay}` : "🎨 Send flash board email"}
    </button>
  );
}

// Attach (not email) the client's flash board to this event. Surfaces the
// booking.defaultFlashProfileId field — previously reachable only as a
// dropdown buried in the Logistics tab — as a first-class action chip next
// to the email chip. Same field, just discoverable. Writes through
// saveFieldInline so the change persists immediately.
function FlashBoardAttachChip({ booking, onAttach }) {
  const [profiles, setProfiles] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const q = query(collection(db, "flashDesignProfiles"), orderBy("name"));
    return onSnapshot(q, snap => {
      setProfiles(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }, () => setProfiles([]));
  }, []);

  const attachedId = booking.defaultFlashProfileId || null;
  const attached = (profiles || []).find(p => p.id === attachedId) || null;
  const attachedName = attached
    ? (attached.clientName ? `${attached.name} — ${attached.clientName}` : attached.name)
    : null;

  const pick = async (id) => {
    setOpen(false);
    if (id === attachedId) return;
    setSaving(true);
    try { await onAttach("defaultFlashProfileId", id); }
    finally { setSaving(false); }
  };

  const rowStyle = (active) => ({
    display: "block", width: "100%", textAlign: "left",
    background: active ? BRAND.bg : "transparent",
    border: "none", color: active ? BRAND.gold : BRAND.cream,
    borderRadius: "6px", padding: "0.5rem 0.6rem",
    fontSize: "0.82rem", fontWeight: active ? 700 : 500, cursor: "pointer",
  });

  return (
    <span style={{ position: "relative", display: "inline-block" }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        disabled={saving}
        title={attachedId ? "Change or remove the flash board attached to this event" : "Attach a client / event flash board to this event"}
        style={{
          background: BRAND.panel,
          border: attachedId ? `1px solid ${BRAND.gold}` : `1px dashed ${BRAND.line}`,
          color: attachedId ? BRAND.gold : BRAND.muted,
          borderRadius: "8px", padding: "0.45rem 0.7rem",
          fontSize: "0.8rem", fontWeight: 700, cursor: saving ? "default" : "pointer",
          maxWidth: "260px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}
      >
        {saving
          ? "Saving…"
          : attachedId
            ? `🎨 Board: ${attachedName || "attached"}`
            : "🎨 Attach flash board"}
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 40 }} />
          <div style={{
            position: "absolute", top: "calc(100% + 4px)", left: 0, zIndex: 41,
            background: BRAND.panel, border: `1px solid ${BRAND.line}`, borderRadius: "10px",
            padding: "0.3rem", minWidth: "260px", maxHeight: "340px", overflowY: "auto",
            boxShadow: "0 8px 24px rgba(0,0,0,0.45)",
          }}>
            {profiles == null ? (
              <div style={{ color: BRAND.muted, padding: "0.5rem", fontSize: "0.82rem" }}>Loading flash sets…</div>
            ) : (
              <>
                <button type="button" onClick={() => pick(null)} style={rowStyle(!attachedId)}>
                  — None (base IP set only) —
                </button>
                {profiles.length === 0 && (
                  <div style={{ color: BRAND.muted, padding: "0.5rem", fontSize: "0.78rem", lineHeight: 1.4 }}>
                    No flash sets yet. Create one in Admin → Designs → Flash Sets, then attach it here.
                  </div>
                )}
                {profiles.map(p => (
                  <button key={p.id} type="button" onClick={() => pick(p.id)} style={rowStyle(p.id === attachedId)}>
                    {p.name}{p.clientName ? ` — ${p.clientName}` : ""} ({(p.designs || []).length})
                  </button>
                ))}
              </>
            )}
          </div>
        </>
      )}
    </span>
  );
}

// 👤 Client card — surfaces the booking↔client link. When linked, shows the
// client + how many events they've booked, with a jump to the client page.
// When unlinked, offers to link to a contact-matched client or create one.
// Writes booking.clientId through saveFieldInline (immediate persist).
function ClientCard({ booking, onLink, navigate }) {
  const clientId = booking.clientId || null;
  const [client, setClient] = useState(null);
  const [count, setCount] = useState(null);
  const [matches, setMatches] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    if (!clientId) { setClient(null); setCount(null); return; }
    (async () => {
      try {
        const c = await getClient(clientId);
        const bks = await getClientBookings(clientId);
        if (alive) { setClient(c); setCount(bks.length); }
      } catch { if (alive) { setClient(null); setCount(null); } }
    })();
    return () => { alive = false; };
  }, [clientId]);

  useEffect(() => {
    let alive = true;
    if (clientId || (!booking.email && !booking.phone)) { setMatches([]); return; }
    (async () => {
      try {
        const m = await findMatchingClients({ email: booking.email, phone: booking.phone });
        if (alive) setMatches(m);
      } catch { if (alive) setMatches([]); }
    })();
    return () => { alive = false; };
  }, [clientId, booking.email, booking.phone]);

  const linkTo = async (id) => { setBusy(true); try { await onLink("clientId", id); } finally { setBusy(false); } };
  const createAndLink = async () => {
    setBusy(true);
    try {
      const id = await createClient({ name: booking.name, company: booking.company, email: booking.email, phone: booking.phone });
      await onLink("clientId", id);
    } finally { setBusy(false); }
  };

  const cardStyle = {
    background: BRAND.panel, border: `1px solid ${BRAND.line}`,
    borderRadius: "10px", padding: "0.9rem", marginBottom: "0.9rem",
  };
  const header = (
    <div style={{ color: BRAND.gold, fontWeight: 700, fontSize: "0.85rem", marginBottom: "0.5rem", letterSpacing: "0.04em" }}>
      👤 CLIENT
    </div>
  );
  const btn = (bg, color, border) => ({
    background: bg, color, border: border || "none",
    borderRadius: "8px", padding: "0.4rem 0.7rem", fontSize: "0.8rem", fontWeight: 700,
    cursor: busy ? "default" : "pointer",
  });

  if (clientId) {
    const display = client ? (client.name || client.company || client.email || "Client") : "Loading…";
    return (
      <div style={cardStyle}>
        {header}
        <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", flexWrap: "wrap" }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ color: BRAND.cream, fontWeight: 700, fontSize: "0.95rem" }}>{display}</div>
            <div style={{ color: BRAND.muted, fontSize: "0.78rem" }}>
              {[client?.company, client?.email, client?.phone].filter(Boolean).join(" · ")}
              {count != null && ` · ${count} event${count === 1 ? "" : "s"}`}
            </div>
          </div>
          <button type="button" disabled={busy} onClick={() => navigate(`/crm/clients/${clientId}`)} style={btn(BRAND.blue, "white")}>
            View client →
          </button>
          <button type="button" disabled={busy} onClick={() => linkTo(null)} style={btn(BRAND.panel, BRAND.muted, `1px solid ${BRAND.line}`)}>
            Detach
          </button>
        </div>
      </div>
    );
  }

  const hasContact = Boolean(booking.email || booking.phone || booking.name);
  return (
    <div style={cardStyle}>
      {header}
      <div style={{ color: BRAND.muted, fontSize: "0.82rem", marginBottom: hasContact ? "0.6rem" : 0 }}>
        {hasContact ? "Not linked to a client." : "Add a name, email, or phone to link this booking to a client."}
      </div>
      {matches.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem", marginBottom: "0.5rem" }}>
          {matches.map((c) => (
            <button key={c.id} type="button" disabled={busy} onClick={() => linkTo(c.id)} style={btn(BRAND.panel, BRAND.gold, `1px solid ${BRAND.gold}`)}>
              🔗 Link to {c.name || c.company || c.email || "client"}
            </button>
          ))}
        </div>
      )}
      {hasContact && (
        <button type="button" disabled={busy} onClick={createAndLink} style={btn(BRAND.green, "white")}>
          {busy ? "Saving…" : "➕ Create client record"}
        </button>
      )}
    </div>
  );
}

// ── Tasks Tab ──────────────────────────────────────────────────────────

const TASK_PRESETS = {
  permit_apply: { emoji: "🏥", label: "Apply for permit", title: "Apply for temp food permit", notes: "Walk into the health department to apply. Keep receipt for payment step." },
  permit_pay: { emoji: "🏥", label: "Pay permit fee", title: "Pay health permit fee", notes: "Walk in to pay within 2 business days of application." },
  send_quote: { emoji: "💬", label: "Send quote", title: "Send pricing quote", notes: "" },
  collect_balance: { emoji: "💵", label: "Collect balance", title: "Collect final balance payment", notes: "" },
};

function QuickTaskButton({ bookingId, preset }) {
  const [busy, setBusy] = useState(false);
  const config = TASK_PRESETS[preset];
  if (!config) return null;

  const onClick = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const fn = httpsCallable(getFunctions(getApp()), "createBookingTask");
      await fn({ bookingId, title: `${config.emoji} ${config.title}`, notes: config.notes || null });
    } catch (err) {
      console.error("Quick task failed:", err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      style={{
        background: BRAND.panel,
        border: `1px solid ${BRAND.line}`,
        color: BRAND.muted,
        borderRadius: "999px",
        padding: "0.3rem 0.65rem",
        fontSize: "0.72rem",
        fontWeight: 600,
        cursor: busy ? "wait" : "pointer",
        opacity: busy ? 0.5 : 1,
      }}
    >
      {config.emoji} {config.label}
    </button>
  );
}

function fmtTaskDate(ts) {
  if (!ts) return null;
  const d = ts.toDate ? ts.toDate() : ts instanceof Date ? ts : new Date(ts);
  if (isNaN(d.getTime())) return null;
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  const tomorrow = new Date(now); tomorrow.setDate(tomorrow.getDate() + 1);
  const isTomorrow = d.toDateString() === tomorrow.toDateString();
  if (isToday) return "Today";
  if (isTomorrow) return "Tomorrow";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function isOverdue(task) {
  if (task.status !== "pending" || !task.dueDate) return false;
  const d = task.dueDate.toDate ? task.dueDate.toDate() : new Date(task.dueDate);
  return d < new Date();
}

function TasksPanel({ bookingId, tasks }) {
  const [acting, setActing] = useState(null);
  const [showAdd, setShowAdd] = useState(false);

  const toggle = async (task) => {
    if (acting) return;
    setActing(task.id);
    try {
      const fnName = task.status === "pending" ? "completeBookingTask" : "uncompleteBookingTask";
      const fn = httpsCallable(getFunctions(getApp()), fnName);
      await fn({ bookingId, taskId: task.id });
    } catch (err) {
      console.error("Task toggle failed:", err.message);
    } finally {
      setActing(null);
    }
  };

  const deleteManual = async (task) => {
    if (acting) return;
    if (!window.confirm(`Delete "${task.title}"?`)) return;
    setActing(task.id);
    try {
      const fn = httpsCallable(getFunctions(getApp()), "deleteBookingTask");
      await fn({ bookingId, taskId: task.id });
    } catch (err) {
      console.error("Task delete failed:", err.message);
    } finally {
      setActing(null);
    }
  };

  const sorted = [...tasks].sort((a, b) => {
    if (a.status !== b.status) return a.status === "pending" ? -1 : 1;
    const aD = a.dueDate?.toDate?.() || a.dueDate || new Date(0);
    const bD = b.dueDate?.toDate?.() || b.dueDate || new Date(0);
    return aD - bD;
  });

  const pending = sorted.filter((t) => t.status === "pending");
  const done = sorted.filter((t) => t.status === "done");

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem" }}>
        <div style={{ color: BRAND.muted, fontSize: "0.75rem" }}>
          {pending.length} pending{done.length > 0 ? ` · ${done.length} done` : ""}
        </div>
        <button
          onClick={() => setShowAdd(true)}
          style={{
            background: "rgba(252,179,31,0.12)",
            color: BRAND.gold,
            border: `1px solid rgba(252,179,31,0.4)`,
            borderRadius: "999px",
            padding: "0.35rem 0.8rem",
            fontSize: "0.78rem",
            fontWeight: 700,
            cursor: "pointer",
          }}
        >
          + Add task
        </button>
      </div>

      {/* Quick-add preset buttons */}
      <div style={{ display: "flex", gap: "0.4rem", flexWrap: "wrap", marginBottom: "0.75rem" }}>
        <QuickTaskButton bookingId={bookingId} preset="permit_apply" />
        <QuickTaskButton bookingId={bookingId} preset="permit_pay" />
        <QuickTaskButton bookingId={bookingId} preset="send_quote" />
        <QuickTaskButton bookingId={bookingId} preset="collect_balance" />
      </div>

      {sorted.length === 0 && (
        <div style={{ color: BRAND.dim, fontSize: "0.88rem", textAlign: "center", padding: "2rem 1rem" }}>
          No tasks yet. Tap "+ Add task" or use a quick-add button above.
        </div>
      )}

      {sorted.map((task) => {
        const overdue = isOverdue(task);
        const isDone = task.status === "done";
        const dateStr = fmtTaskDate(task.dueDate);
        return (
          <div
            key={task.id}
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: "0.6rem",
              padding: "0.65rem 0.75rem",
              background: isDone ? "transparent" : overdue ? "rgba(201,68,68,0.08)" : BRAND.panel,
              border: `1px solid ${overdue && !isDone ? "rgba(201,68,68,0.35)" : BRAND.line}`,
              borderRadius: "10px",
              marginBottom: "0.5rem",
              opacity: isDone ? 0.55 : 1,
            }}
          >
            <button
              onClick={() => toggle(task)}
              disabled={!!acting}
              style={{
                flexShrink: 0,
                width: 22, height: 22,
                borderRadius: "6px",
                border: `2px solid ${isDone ? BRAND.green : overdue ? "#c94444" : BRAND.gold}`,
                background: isDone ? BRAND.green : "transparent",
                cursor: acting ? "wait" : "pointer",
                display: "flex", alignItems: "center", justifyContent: "center",
                marginTop: "0.1rem",
                fontSize: "0.7rem",
                color: "#fff",
              }}
            >
              {isDone ? "✓" : ""}
            </button>

            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                color: isDone ? BRAND.dim : BRAND.text,
                fontSize: "0.88rem",
                fontWeight: 600,
                textDecoration: isDone ? "line-through" : "none",
                lineHeight: 1.3,
              }}>
                {task.title}
              </div>
              {dateStr && (
                <div style={{
                  fontSize: "0.72rem",
                  fontWeight: 600,
                  color: isDone ? BRAND.dim : overdue ? "#c94444" : dateStr === "Today" ? BRAND.gold : BRAND.muted,
                  marginTop: "0.2rem",
                }}>
                  {overdue && !isDone ? "⚠ Overdue · " : ""}{dateStr}
                </div>
              )}
              {task.notes && (
                <div style={{
                  color: BRAND.dim,
                  fontSize: "0.72rem",
                  marginTop: "0.25rem",
                  lineHeight: 1.4,
                  whiteSpace: "pre-wrap",
                  maxHeight: isDone ? 0 : "4rem",
                  overflow: "hidden",
                }}>
                  {task.notes}
                </div>
              )}
            </div>

            <button
              onClick={() => deleteManual(task)}
              disabled={!!acting}
              style={{
                background: "transparent",
                border: "none",
                color: BRAND.dim,
                cursor: "pointer",
                fontSize: "0.8rem",
                padding: "0.2rem",
                flexShrink: 0,
              }}
              title="Delete task"
            >
              ×
            </button>
          </div>
        );
      })}

      {showAdd && <AddTaskModal bookingId={bookingId} onClose={() => setShowAdd(false)} />}
    </div>
  );
}

function AddTaskModal({ bookingId, onClose }) {
  const [title, setTitle] = useState("");
  const [notes, setNotes] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);

  const create = async (e) => {
    e.preventDefault();
    if (creating || !title.trim()) return;
    setCreating(true);
    setError(null);
    try {
      const fn = httpsCallable(getFunctions(getApp()), "createBookingTask");
      await fn({ bookingId, title: title.trim(), notes: notes.trim() || null, dueDate: dueDate || null });
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" }}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={create} style={{ background: BRAND.bg, border: `1px solid ${BRAND.line}`, borderRadius: "14px", padding: "1.2rem", maxWidth: "420px", width: "100%" }}>
        <div style={{ color: BRAND.gold, fontWeight: 700, fontSize: "0.88rem", marginBottom: "1rem" }}>New task</div>

        <label style={{ display: "block", color: BRAND.muted, fontSize: "0.72rem", fontWeight: 600, marginBottom: "0.3rem" }}>Title *</label>
        <input
          value={title} onChange={(e) => setTitle(e.target.value)}
          placeholder="What needs to happen?"
          required autoFocus
          style={{ width: "100%", boxSizing: "border-box", background: BRAND.panel, color: BRAND.text, border: `1px solid ${BRAND.line}`, borderRadius: "8px", padding: "0.6rem 0.75rem", fontSize: "0.88rem", marginBottom: "0.75rem" }}
        />

        <label style={{ display: "block", color: BRAND.muted, fontSize: "0.72rem", fontWeight: 600, marginBottom: "0.3rem" }}>Due date</label>
        <input
          type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)}
          style={{ background: BRAND.panel, color: BRAND.text, border: `1px solid ${BRAND.line}`, borderRadius: "8px", padding: "0.5rem 0.75rem", fontSize: "0.85rem", marginBottom: "0.75rem" }}
        />

        <label style={{ display: "block", color: BRAND.muted, fontSize: "0.72rem", fontWeight: 600, marginBottom: "0.3rem" }}>Notes</label>
        <textarea
          value={notes} onChange={(e) => setNotes(e.target.value)}
          rows={3} placeholder="Optional context"
          style={{ width: "100%", boxSizing: "border-box", background: BRAND.panel, color: BRAND.text, border: `1px solid ${BRAND.line}`, borderRadius: "8px", padding: "0.6rem 0.75rem", fontSize: "0.85rem", resize: "vertical", fontFamily: "inherit" }}
        />

        {error && <div style={{ color: "#c94444", fontSize: "0.8rem", marginTop: "0.5rem" }}>{error}</div>}

        <div style={{ display: "flex", gap: "0.5rem", marginTop: "1rem", justifyContent: "flex-end" }}>
          <button type="button" onClick={onClose} style={{ background: "transparent", color: BRAND.muted, border: `1px solid ${BRAND.line}`, borderRadius: "8px", padding: "0.5rem 1rem", fontSize: "0.82rem", cursor: "pointer" }}>
            Cancel
          </button>
          <button type="submit" disabled={creating || !title.trim()} style={{ background: BRAND.gold, color: "#1a0a12", border: "none", borderRadius: "8px", padding: "0.5rem 1rem", fontSize: "0.82rem", fontWeight: 700, cursor: creating ? "wait" : "pointer" }}>
            {creating ? "Creating…" : "Create task"}
          </button>
        </div>
      </form>
    </div>
  );
}

